import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { SortOrder } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    SwapRightOutlined,
} from "@ant-design/icons";


import { IRecoverRequest } from "../../interfaces";
import { formatCpf, formatPhone } from "../../utils/Masks";
import { useRecoverRequestsContext } from "../../contexts/RecoverRequestsContext";
import { formatDateStringToDateAndTime } from "../../utils/Conversores";
import { removeAcento } from "../../utils/RemoveAcentos";
import { ThemeContext } from "../../contexts/ThemeContext";
import TableActionButton from "../../components/TableActionButton";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";
import useTableColumnFilter from "../../hooks/useTableColumnFilter";

export interface ITableData extends IRecoverRequest {
    key: number | undefined;
    app_user_name: string;
    app_user_cpf: string;
}

export interface IRecoverRequestFilters {
    search: string | null;
    status: "ANALYSIS" | "APPROVED" | "REFUSED" | null;
}

export type SortField = "app_user_name" | "app_user_cpf" | "status" | "created_at" | "date_response" | null;

export default function useRecoverRequests() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const { getColumnSearchProps, getColumnRadioProps } = useTableColumnFilter();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rawData, setRawData] = useState<Array<ITableData>>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });

    const [filters, setFilters] = useState<IRecoverRequestFilters>({
        search: null,
        status: null,
    });

    const [sorter, setSorter] = useState<{ field: SortField; order: "asc" | "desc" | null }>({
        field: "created_at",
        order: "desc",
    });

    const [searchQuery, setSearchQuery] = useState("");

    const navigate = useNavigate();
    const { recoverRequests, loadRecoverRequests } = useRecoverRequestsContext();

    const handleClickViewRequest = useCallback(
        (data: IRecoverRequest) => {
            navigate(`/recuperacao-acesso/${data.id}`);
        },
        [navigate]
    );

    const handleFilterChange = useCallback((key: keyof IRecoverRequestFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({
            search: null,
            status: null,
        });
        setSearchQuery("");
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const stats = useMemo(() => {
        const total = rawData.length;
        const pendentes = rawData.filter((r) => r.status === "Em Análise" || !r.status).length;
        const aprovados = rawData.filter((r) => r.status === "Aprovado!" || r.status === "Aprovado").length;
        const recusados = rawData.filter((r) => r.status === "Recusado!" || r.status === "Recusado").length;

        return { total, pendentes, aprovados, recusados };
    }, [rawData]);

    const filteredData = useMemo(() => {
        let result = rawData;

        if (filters.search) {
            const query = removeAcento(filters.search.trim().toLowerCase());
            result = result.filter((item) => {
                const name = item.app_user_name ? removeAcento(item.app_user_name.toLowerCase()) : "";
                const cpf = item.app_user_cpf ? item.app_user_cpf.replace(/\D/g, "") : "";
                const rawCpf = item.app_user_cpf || "";
                const existingPhone = item.existing_phone ? item.existing_phone.replace(/\D/g, "") : "";
                const newPhone = item.new_phone ? item.new_phone.replace(/\D/g, "") : "";

                return (
                    name.includes(query) ||
                    cpf.includes(query) ||
                    rawCpf.includes(query) ||
                    existingPhone.includes(query) ||
                    newPhone.includes(query)
                );
            });
        }

        if (filters.status) {
            if (filters.status === "ANALYSIS") {
                result = result.filter((item) => item.status === "Em Análise" || !item.status);
            } else if (filters.status === "APPROVED") {
                result = result.filter((item) => item.status === "Aprovado!" || item.status === "Aprovado");
            } else if (filters.status === "REFUSED") {
                result = result.filter((item) => item.status === "Recusado!" || item.status === "Recusado");
            }
        }

        return result;
    }, [rawData, filters]);

    const sortedData = useMemo(() => {
        if (!sorter.field || !sorter.order) return filteredData;

        return [...filteredData].sort((a, b) => {
            let valA: any = a[sorter.field as keyof ITableData];
            let valB: any = b[sorter.field as keyof ITableData];

            if (sorter.field === "created_at" || sorter.field === "date_response") {
                valA = valA ? new Date(valA).getTime() : 0;
                valB = valB ? new Date(valB).getTime() : 0;
            } else if (typeof valA === "string") {
                valA = removeAcento(valA.toLowerCase());
                valB = removeAcento((valB || "").toLowerCase());
            }

            if (valA < valB) return sorter.order === "asc" ? -1 : 1;
            if (valA > valB) return sorter.order === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredData, sorter]);

    const paginatedData = useMemo(() => {
        const start = (pagination.current - 1) * pagination.pageSize;
        return sortedData.slice(start, start + pagination.pageSize);
    }, [sortedData, pagination]);

    const activeFilterItems: IActiveFilterItem[] = useMemo(() => {
        const items: IActiveFilterItem[] = [];

        if (filters.search) {
            items.push({
                key: "search",
                label: "Busca",
                value: `"${filters.search}"`,
                onRemove: () => {
                    handleFilterChange("search", null);
                    setSearchQuery("");
                },
            });
        }

        if (filters.status) {
            const statusLabels: Record<string, string> = {
                ANALYSIS: "Em Análise",
                APPROVED: "Aprovados",
                REFUSED: "Recusados",
            };
            items.push({
                key: "status",
                label: "Status",
                value: statusLabels[filters.status],
                onRemove: () => handleFilterChange("status", null),
            });
        }

        return items;
    }, [filters, handleFilterChange]);

    const handleTableChange = useCallback((newPagination: any, tableFilters: any, newSorter: any) => {
        if (Array.isArray(newSorter)) {
            newSorter = newSorter[0];
        }

        if (newSorter && newSorter.field) {
            const order = newSorter.order === "ascend" ? "asc" : newSorter.order === "descend" ? "desc" : null;
            setSorter({
                field: order ? (newSorter.field as SortField) : null,
                order,
            });
        } else {
            setSorter({ field: null, order: null });
        }
    }, []);

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const columns: ColumnsType<ITableData> = useMemo(() => {
        const getSortOrder = (field: SortField): SortOrder | undefined => {
            if (sorter.field !== field) return undefined;
            return sorter.order === "asc" ? "ascend" : sorter.order === "desc" ? "descend" : undefined;
        };

        return [
            {
                title: "Cidadão / Solicitante",
                dataIndex: "app_user_name",
                key: "app_user_name",
                sorter: true,
                sortOrder: getSortOrder("app_user_name"),
                ...getColumnSearchProps({
                    title: "Filtrar por Cidadão",
                    placeholder: "Nome ou CPF...",
                    value: searchQuery,
                    onChange: setSearchQuery,
                    onSearch: (val) => handleFilterChange("search", val),
                    onClear: () => handleFilterChange("search", null),
                    isActive: Boolean(filters.search),
                }),
                render: (_: any, record: ITableData) => (
                    <div>
                        <div style={{ fontWeight: 600, color: isDark ? "#f0f0f0" : "#1e293b", fontSize: 13.5 }}>
                            <Highlighter
                                highlightStyle={{ backgroundColor: "#ffc069", padding: "0 2px" }}
                                searchWords={filters.search ? [filters.search] : []}
                                autoEscape
                                textToHighlight={record.app_user_name || "--"}
                            />
                        </div>
                        <div style={{ fontSize: 12, color: isDark ? "#8c8c8c" : "#64748b", marginTop: 1 }}>
                            CPF: {formatCpf(record.app_user_cpf) || "--"}
                        </div>
                    </div>
                ),
            },
            {
                title: "Alteração de Telefone",
                key: "phone_change",
                render: (_: any, record: ITableData) => (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap" }}>
                        <Tooltip title="Telefone atual cadastrado">
                            <Tag
                                style={{
                                    margin: 0,
                                    fontSize: 11.5,
                                    background: isDark ? "#222" : "#f1f5f9",
                                    borderColor: isDark ? "#333" : "#cbd5e1",
                                    color: isDark ? "#aaa" : "#64748b",
                                    fontFamily: "monospace",
                                }}
                            >
                                {formatPhone(record.existing_phone) || "--"}
                            </Tag>
                        </Tooltip>
                        <SwapRightOutlined style={{ color: "#fa8c16", fontSize: 14, flexShrink: 0 }} />
                        <Tooltip title="Novo telefone solicitado">
                            <Tag
                                color="blue"
                                style={{
                                    margin: 0,
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    fontFamily: "monospace",
                                }}
                            >
                                {formatPhone(record.new_phone) || "--"}
                            </Tag>
                        </Tooltip>
                    </div>
                ),
            },
            {
                title: "Status",
                dataIndex: "status",
                key: "status",
                width: 130,
                sorter: true,
                sortOrder: getSortOrder("status"),

                ...getColumnRadioProps({
                    title: "Filtrar por Status",
                    value: filters.status || "",
                    onChange: (val) => handleFilterChange("status", val || null),
                    onClear: () => handleFilterChange("status", null),
                    isActive: Boolean(filters.status),
                    options: [
                        { label: "Todos os Status", value: "" },
                        { label: "Em Análise", value: "ANALYSIS" },
                        { label: "Aprovado", value: "APPROVED" },
                        { label: "Recusado", value: "REFUSED" },
                    ],
                }),
                render: (_: any, record: ITableData) => {
                    const status = record.status;
                    if (status === "Aprovado!" || status === "Aprovado") {
                        return (
                            <Tag color="success" icon={<CheckCircleOutlined />} style={{ borderRadius: 4, fontWeight: 500 }}>
                                Aprovado
                            </Tag>
                        );
                    }
                    if (status === "Recusado!" || status === "Recusado") {
                        return (
                            <Tag color="error" icon={<CloseCircleOutlined />} style={{ borderRadius: 4, fontWeight: 500 }}>
                                Recusado
                            </Tag>
                        );
                    }
                    return (
                        <Tag color="warning" icon={<ClockCircleOutlined />} style={{ borderRadius: 4, fontWeight: 500 }}>
                            Em Análise
                        </Tag>
                    );
                },
            },
            {
                title: "Data Solicitação",
                dataIndex: "created_at",
                key: "created_at",
                width: 160,
                sorter: true,
                sortOrder: getSortOrder("created_at"),
                render: (val: string) => (
                    <span style={{ fontSize: 12.5, color: isDark ? "#d9d9d9" : "#475569", whiteSpace: "nowrap" }}>
                        {val ? formatDateStringToDateAndTime(val) : "--"}
                    </span>
                ),
            },
            {
                title: "Data Resposta",
                dataIndex: "date_response",
                key: "date_response",
                width: 160,
                sorter: true,
                sortOrder: getSortOrder("date_response"),
                render: (val: string, record: ITableData) => {
                    if (!val) {
                        return <span style={{ color: isDark ? "#666" : "#94a3b8", fontSize: 12 }}>Aguardando...</span>;
                    }
                    const formatted = formatDateStringToDateAndTime(val);
                    return record.panel_user?.name ? (
                        <Tooltip title={`Respondido por: ${record.panel_user.name}`}>
                            <span style={{ fontSize: 12.5, color: isDark ? "#d9d9d9" : "#475569", cursor: "help", whiteSpace: "nowrap" }}>
                                {formatted}
                            </span>
                        </Tooltip>
                    ) : (
                        <span style={{ fontSize: 12.5, color: isDark ? "#d9d9d9" : "#475569", whiteSpace: "nowrap" }}>
                            {formatted}
                        </span>
                    );
                },
            },
            {
                title: "Ações",
                key: "actions",
                width: 80,
                align: "center" as const,
                render: (_: any, record: ITableData) => (
                    <TableActionButton
                        action="view"
                        tooltip="Visualizar e Analisar Solicitação"
                        onClick={() => handleClickViewRequest(record)}
                    />
                ),
            },
        ];
    }, [
        isDark,
        sorter,
        searchQuery,
        filters.search,
        filters.status,
        getColumnSearchProps,
        getColumnRadioProps,
        handleFilterChange,
        handleClickViewRequest,
    ]);


    const setTableDataFromRequest = useCallback((data: Array<IRecoverRequest>) => {
        const newData: ITableData[] = data.map((request) => ({
            ...request,
            key: request.id,
            app_user_name: request.app_user?.name || "",
            app_user_cpf: request.app_user?.cpf || "",
        }));
        setRawData(newData);
    }, []);

    useEffect(() => {
        setTableDataFromRequest(recoverRequests);
    }, [recoverRequests, setTableDataFromRequest]);

    const reloadData = useCallback(async () => {
        const controller = new AbortController();
        setIsLoading(true);
        await loadRecoverRequests(controller);
        setIsLoading(false);
    }, [loadRecoverRequests]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadData() {
            setIsLoading(true);
            await loadRecoverRequests(controller);
            setIsLoading(false);
        }

        loadData();

        return () => {
            controller.abort();
        };
    }, [loadRecoverRequests]);

    return {
        isLoading,
        columns,
        tableData: paginatedData,
        totalCount: sortedData.length,
        stats,
        pagination: {
            ...pagination,
            total: sortedData.length,
        },
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        handleClickViewRequest,
        reloadData,
    };
}
